import { useForm } from 'react-hook-form';
import { Button, Icon, Input, InputWrapper } from '@frontend/ui-library';
import { getIdFromUrl } from '@frontend/utils';

import { ReactComponent as ArrowRightIcon } from '@frontend/icons/arrow-right.svg';

import useUpdateBuyerDetails from '../../hooks/useUpdateBuyerDetails';
import { useCountries } from '../../hooks/useCountries';
import CountryDropdown from '../../components/CountryDropdown';
import styles from './BuyerInfo.module.css';

import { PageFields_BuyerDetailsUpdatePage_Fragment as BuyerDetailsUpdatePageFragment } from '../../graphql/generated';
import { useState } from 'react';
import { CountryCodeEnum, isTaxIdValid } from '../../utils';

export interface BuyerInfoFormData {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  country: string;
  taxNumber: string;
}

interface BuyerInfoFormProps {
  onSubmit?: (data: BuyerInfoFormData) => void;
  data: BuyerDetailsUpdatePageFragment['pageData'];
}

function BuyerInfoForm({ onSubmit, data }: BuyerInfoFormProps) {
  const [isReceivingResponse, setIsReceivingResponse] = useState(false);
  const {
    data: countries,
    isLoading: isLoadingCountries,
    error: countriesError,
  } = useCountries();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<BuyerInfoFormData>({
    mode: 'onSubmit',
    defaultValues: {
      firstName: data.firstName ?? undefined,
      lastName: data.lastName ?? undefined,
      address: data.residentialAddress ?? undefined,
      city: data.city ?? undefined,
      country: data.residentialCountry ?? undefined,
      taxNumber: undefined,
    },
  });

  const countryValue = watch('country');

  const { updateBuyerDetails } = useUpdateBuyerDetails();

  const handleFormSubmit = async (formData: BuyerInfoFormData) => {
    try {
      const id = getIdFromUrl();
      setIsReceivingResponse(true);
      await updateBuyerDetails({
        variables: {
          id,
          type: 'bill',
          firstName: formData.firstName,
          lastName: formData.lastName,
          city: formData.city,
          residentialAddress: formData.address,
          residentialCountry: formData.country,
          taxNumber: formData.taxNumber || '',
        },
      });
      onSubmit?.(formData);
    } catch (error) {
      setIsReceivingResponse(false);
    }
  };

  const handleCountryChange = (value: string) => {
    setValue('country', value, { shouldValidate: true });
  };

  const adaptedCountries =
    countries?.map((country) => ({
      alpha2code: country.attributes.alpha2,
      name: country.attributes.name,
    })) || [];

  return (
    <form className={styles['form']} onSubmit={handleSubmit(handleFormSubmit)}>
      <div className={styles['form-container']}>
        <div className={styles['form-row']}>
          <InputWrapper
            error={errors.firstName?.message}
            className={styles['form-input']}
          >
            <Input
              label="First name"
              error={!!errors.firstName}
              {...register('firstName', {
                required: 'Required field',
              })}
            />
          </InputWrapper>
          <InputWrapper
            error={errors.lastName?.message}
            className={styles['form-input']}
          >
            <Input
              label="Last name"
              error={!!errors.lastName}
              {...register('lastName', {
                required: 'Required field',
              })}
            />
          </InputWrapper>
        </div>
        <InputWrapper error={errors.address?.message}>
          <Input
            className={styles['form-input']}
            label="Residential address"
            error={!!errors.address}
            {...register('address', {
              required: 'Required field',
            })}
          />
        </InputWrapper>
        <InputWrapper error={errors.city?.message}>
          <Input
            className={styles['form-input']}
            label="City"
            error={!!errors.city}
            {...register('city', {
              required: 'Required field',
            })}
          />
        </InputWrapper>
        <InputWrapper error={errors.country?.message}>
          <CountryDropdown
            countries={adaptedCountries}
            value={countryValue}
            onValueChange={handleCountryChange}
            placeholder={
              isLoadingCountries ? 'Loading countries...' : 'Select country'
            }
            disabled={isLoadingCountries || !!countriesError}
            {...register('country', {
              required: 'Required field',
            })}
          />
        </InputWrapper>
        {data.taxNumberRequired && (
          <InputWrapper error={errors.taxNumber?.message}>
            <Input
              className={styles['form-input']}
              label="Tax number"
              error={!!errors.taxNumber}
              {...register('taxNumber', {
                required: data.taxNumberRequired ? 'Required field' : false,
                validate: (value) => {
                  if (!value)
                    return data.taxNumberRequired ? 'Required field' : true;
                  const selectedCountry = countryValue as CountryCodeEnum;
                  return (
                    isTaxIdValid(value, selectedCountry) || 'Invalid tax number'
                  );
                },
              })}
            />
          </InputWrapper>
        )}
        <Button
          rightIcon={<Icon component={<ArrowRightIcon />} />}
          size="large"
          variant="primary"
          type="submit"
          loading={isReceivingResponse}
          disabled={isSubmitting}
        >
          Continue
        </Button>
      </div>
    </form>
  );
}

export default BuyerInfoForm;
